import {
    defineComponent,
    PropType,
    ref,
    watch,
} from "vue";
import { DefaultEntityClient } from "../core/api/EntityClient.js";
import type { LogRole } from "../core/types.js";
import Store from "../store.js";
import {
    getRolePrimaryColorCss,
    roleColorGradientToCss,
} from "../shared/roles/roleColors.js";

const RoleCacheByGuild = new Map<string, Promise<Map<string, LogRole>>>();

function loadGuildRoles(guildId: string): Promise<Map<string, LogRole>> {
    let cached = RoleCacheByGuild.get(guildId);

    if (!cached) {
        cached = DefaultEntityClient.getRoles()
            .then((roles) => new Map(roles.map((role) => [ role.id, role ])))
            .catch(() => new Map<string, LogRole>());

        RoleCacheByGuild.set(guildId, cached);
    }

    return cached;
}

function getRoleIconUrl(role: LogRole): string | null {
    const icon = role.iconURL ?? role.icon;

    if (!icon)
        return null;

    if (icon.startsWith("http://") || icon.startsWith("https://"))
        return icon;

    return `https://cdn.discordapp.com/role-icons/${role.id}/${icon}.webp?size=64`;
}

function getRoleEmoji(role: LogRole): string | null {
    return role.unicodeEmoji ?? role.unciodeEmoji ?? null;
}

function getFallbackRole(id: string, fallbackLabel: string | null): LogRole {
    const isEveryone = id === Store.selectedGuild?.id;

    return {
        id,
        name: fallbackLabel || (isEveryone ? "@everyone" : `Rôle ${id}`),
    };
}

export default defineComponent({
    props: {
        id: {
            type: String,
            required: true,
        },

        fallbackLabel: {
            type: String as PropType<string | null>,
            default: null,
        },
    },

    setup(props) {
        const role = ref<LogRole | null>(null);

        watch(
            () => [ Store.selectedGuild?.id, props.id ] as const,
            async ([ guildId, roleId ]) => {
                role.value = null;

                if (!guildId || !roleId)
                    return;

                role.value = (await loadGuildRoles(guildId)).get(roleId) ?? null;
            },
            { immediate: true },
        );

        return () => {
            const resolvedRole = role.value ?? getFallbackRole(props.id, props.fallbackLabel);
            const roleColor = getRolePrimaryColorCss(resolvedRole);
            const roleBackground = roleColorGradientToCss(resolvedRole);
            const iconUrl = getRoleIconUrl(resolvedRole);
            const emoji = getRoleEmoji(resolvedRole);

            return (
                <router-link
                    to={`/roles/${props.id}`}
                    class="markdown-role-mention"
                    style={{
                        "--role-color"          : roleColor,
                        "--role-dot-background" : roleBackground,
                    } as Record<string, string>}>
                    <span class="markdown-role-mention__dot" />
                    {iconUrl && <img class="markdown-role-mention__icon" src={iconUrl} alt="" />}
                    {!iconUrl && emoji && <span class="markdown-role-mention__emoji">{emoji}</span>}
                    <span>{resolvedRole.name}</span>
                </router-link>
            );
        };
    },
});
